/**
 * Payment Success Page
 * Shown after successful Stripe checkout
 */
import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2 } from "lucide-react";

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [planType, setPlanType] = useState(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId && token) {
      verifyPayment();
    }
  }, [sessionId, token]);

  const verifyPayment = async () => {
    try {
      const response = await fetch(
        `${API}/subscriptions/checkout/status/${sessionId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === "paid") {
          setStatus("success");
          setPlanType(data.plan_type);
          // Refresh user data to update subscription status
          if (refreshUser) {
            await refreshUser();
          }
        } else {
          setStatus("pending");
        }
      } else {
        setStatus("error");
      }
    } catch (error) {
      console.error("Verification error:", error);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {status === "loading" && (
          <>
            <Loader2 className="w-16 h-16 text-white/60 animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-2">
              Confirming your payment...
            </h1>
            <p className="text-white/60">Please wait while we verify your subscription.</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 bg-coral-dim rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-coral" />
            </div>
            <h1 className="text-3xl font-bold mb-2">
              Payment Successful!
            </h1>
            <p className="text-white/60 mb-8">
              {planType === "studio_monthly" && "You now have unlimited access to browse second shooters."}
              {planType === "second_monthly" && "You can now browse and apply to job listings."}
              {planType === "studio_per_job" && "You can now view candidates for your job listing."}
            </p>
            <div className="space-y-3">
              {planType === "studio_per_job" ? (
                <Link to="/jobs/my-jobs">
                  <Button className="w-full bg-white text-black hover:bg-gray-200 h-12 font-bold rounded-sm">
                    View My Jobs
                  </Button>
                </Link>
              ) : planType === "second_monthly" ? (
                <Link to="/jobs/browse">
                  <Button className="w-full bg-white text-black hover:bg-gray-200 h-12 font-bold rounded-sm">
                    Browse Jobs
                  </Button>
                </Link>
              ) : (
                <Link to="/jobs/create">
                  <Button className="w-full bg-white text-black hover:bg-gray-200 h-12 font-bold rounded-sm">
                    Post a Job
                  </Button>
                </Link>
              )}
              <Link to="/dashboard">
                <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10 h-12 rounded-sm">
                  Go to Dashboard
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === "pending" && (
          <>
            <Loader2 className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-2">
              Payment Processing
            </h1>
            <p className="text-white/60 mb-8">
              Your payment is being processed. This may take a moment.
            </p>
            <Button onClick={verifyPayment} className="bg-white text-black hover:bg-gray-200">
              Check Again
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">⚠️</span>
            </div>
            <h1 className="text-2xl font-bold mb-2">
              Something went wrong
            </h1>
            <p className="text-white/60 mb-8">
              We couldn't verify your payment. Please contact support if you were charged.
            </p>
            <Link to="/dashboard">
              <Button className="bg-white text-black hover:bg-gray-200">
                Go to Dashboard
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
